import React from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useTable, useSortBy, usePagination, useFilters, useGlobalFilter } from "react-table"

import { privateRoutes } from "router/routes"

import { ArrowIcon } from "components/Elements/Icons"
import Emoji from "components/shared/Emoji"
import dateService from "services/date"
import { isDefined } from "utils/validation"
import {
	ONE_DAY_IN_SECONDS,
	BACKSTOSTORE_REQUEST,
	BUY_REQUEST,
	CANCELLED_REQUEST,
} from "utils/constants"

import {
	PORTABILITY_REQUEST,
	PORTABILITY_REQUEST_REFUSED,
	PORTABILITY_REQUEST_CANCELLED,
} from "utils/constants"

const PAGINATION_LENGTH = 15

export const HistoryTable = ({ data, bikeStatus, className = "" }) => {
	const { t } = useTranslation()

	const bikeDescriptionAccessor =
		bikeStatus === "PRE-SAVED-BIKES" ? "description_data" : "bike_description"

	const getIsExpiredDate = (bike) => {
		return (
			bikeStatus === "ON-GOING" &&
			(bike.operation === BACKSTOSTORE_REQUEST || bike.operation === BUY_REQUEST) &&
			dateService.getTimestamp(bike.create_date) + ONE_DAY_IN_SECONDS < new Date().getTime()
		)
	}

	// Columns definition
	const columns = React.useMemo(
		() => {
			const baseCols = [
				{
					Header: t("history.activity_headers.date", { ns: "partner" }),
					accessor: bikeStatus === "PRE-SAVED-BIKES" ? "modify_date" : "create_date",
					Cell: ({ value }) =>
						isDefined(value) &&
						dateService.formatDate(value, dateService.getCurrentDateFormat()),
				},
				{
					Header: t("history.activity_headers.code", { ns: "partner" }),
					accessor: "bike_id",
					Cell: ({ row, value }) => {
						// If isPortabilityRequest, added search param with bike_id in url to launch search on FormSearchBike component
						const isPortabilityRequest = [
							PORTABILITY_REQUEST,
							PORTABILITY_REQUEST_REFUSED,
							PORTABILITY_REQUEST_CANCELLED,
						].includes(row.original.operation)

						return (
							<>
								{isPortabilityRequest && (
									<Link
										className="underline"
										to={{
											pathname: `${privateRoutes.searchBike.path}`,
											search: `?bikeCode=${row.original.bike_id}`,
										}}
									>
										{value}
									</Link>
								)}

								{!isPortabilityRequest && (
									<Link
										className="underline"
										to={{
											pathname: `${privateRoutes.bike.basePath}/${value}`,
										}}
									>
										{value}
									</Link>
								)}
							</>
						)
					},
				},
				{
					Header: t("history.activity_headers.brand", { ns: "partner" }),
					accessor: `${bikeDescriptionAccessor}.brand`,
				},
				{
					Header: t("history.activity_headers.model", { ns: "partner" }),
					accessor: `${bikeDescriptionAccessor}.model`,
				},
				{
					Header: t("history.activity_headers.vae", { ns: "partner" }),
					accessor: `${bikeDescriptionAccessor}.is_electric`,
					Cell: ({ value }) => (value === true ? t("global.yes") : t("global.no")),
				},
				{
					Header: t("history.activity_headers.color", { ns: "partner" }),
					accessor: `${bikeDescriptionAccessor}.color`,
				},
				{
					Header: t("history.activity_headers.manufacturer_name", { ns: "partner" }),
					accessor: `${bikeDescriptionAccessor}.manufacturer_name`,
				},
				{
					Header: t("history.activity_headers.operation", { ns: "partner" }),
					accessor: "operation",
					Cell: ({ row, value }) => (
						<>
							{getIsExpiredDate(row.original) ? (
								<>
									<div className="font-bold">
										{t("history.operation.backtostore-expired", {
											ns: "partner",
										})}
									</div>
									<Link
										className="underline flex items-center"
										to={{
											pathname: `${privateRoutes.bike.basePath}/${row.original.bike_id}`,
										}}
									>
										<ArrowIcon className="mr-1" size={16} />{" "}
										{t("history.operation.required-action", { ns: "partner" })}
									</Link>
								</>
							) : (
								<div>
									{value
										? row.original.operation === CANCELLED_REQUEST
											? t("history.operation.cancelled", { ns: "partner" })
											: value
										: t("history.operation.pre-registered", { ns: "partner" })}
								</div>
							)}
						</>
					),
				},
			]
			return baseCols
		},
		[bikeDescriptionAccessor, bikeStatus, t] // eslint-disable-line
	)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page, // the rows for the active page

		// pagination utilities
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		state: { pageIndex },
	} = useTable(
		{
			//@ts-ignore
			columns,
			data,
			initialState: {
				pageSize: 15,
			},
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		usePagination
	)

	// Manage pagination
	// -------------------
	const middleDistance = Math.round((PAGINATION_LENGTH - 1) / 2)
	const pageShouldBeHiddenInPagination = (index) => {
		// in the first pages,
		// pagination should not exceed PAGINATION_LENGTH
		const condition1 = pageIndex <= middleDistance && index > PAGINATION_LENGTH - 1
		// in the middle pages,
		// show x pages before currentPage and y pages after currentPage
		const condition2 =
			pageIndex > middleDistance &&
			pageIndex < pageOptions.length - middleDistance &&
			(index < pageIndex - middleDistance || index > pageIndex + middleDistance - 1)
		// in the last pages,
		// always show the x last pages
		const condition3 =
			pageIndex > pageOptions.length - 1 - middleDistance &&
			index < pageOptions.length - PAGINATION_LENGTH
		return condition1 || condition2 || condition3
	}

	return (
		<>
			<div className={`c-history__table ${className}`}>
				<div className="c-grid-table">
					<table className="c-grid-table__wrapper" {...getTableProps()}>
						<thead>
							{headerGroups.map((headerGroup) => (
								<tr
									className="c-grid-table__head flex uppercase"
									{...headerGroup.getHeaderGroupProps()}
								>
									{headerGroup.headers.map((column) => {
										return (
											<th
												className={"c-table__cell p-4 w-[160px]"}
												{...column.getHeaderProps(
													column.getSortByToggleProps()
												)}
											>
												{column.render("Header")}
												<span>
													{column.isSorted
														? column.isSortedDesc
															? " 🔽"
															: " 🔼"
														: ""}
												</span>
											</th>
										)
									})}
								</tr>
							))}
						</thead>
						<tbody {...getTableBodyProps()}>
							{page.map((row) => {
								prepareRow(row)
								return (
									<tr
										className={`c-table__row p-5 lg:p-0 ${
											getIsExpiredDate(row.original) ? "is-expired" : ""
										}`}
										{...row.getRowProps()}
									>
										{row.cells.map((cell) => {
											return (
												<td
													className={"c-table__cell p-4 w-[160px]"}
													{...cell.getCellProps()}
												>
													{cell.render("Cell")}
												</td>
											)
										})}
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>

				{data.length === 0 && (
					<p className="text-center mt-5">
						{t("history.no_result", { ns: "partner" })}{" "}
						<Emoji label="visage triste" symbol="😟" />
					</p>
				)}

				{/* Pagination */}
				{pageOptions && pageOptions.length > 1 && (
					<div className="flex justify-center mt-5">
						<div className="c-pagination">
							<button
								className="c-btn c-btn--primary px-2 mx-5"
								onClick={() => gotoPage(0)}
								disabled={!canPreviousPage}
							>
								{"<<"}
							</button>
							{pageOptions.map((index) => {
								if (pageShouldBeHiddenInPagination(index))
									return <React.Fragment key={index}></React.Fragment>
								return (
									<button
										type="button"
										key={index}
										className={
											"text-center " +
											(index === pageIndex ? "font-bold" : "")
										}
										style={{ width: "30px" }}
										onClick={() => gotoPage(index)}
									>
										{index + 1}
									</button>
								)
							})}
							<button
								className="c-btn c-btn--primary px-2 mx-5"
								onClick={() => gotoPage(pageCount - 1)}
								disabled={!canNextPage}
							>
								{">>"}
							</button>
						</div>
					</div>
				)}
			</div>
		</>
	)
}
